import React from 'react';
import styled from 'styled-components';
import VipIcon from '../../Icons/VipIcons';
import { formatMediaDuration } from '../../../utils/formatters';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns || 3}, 1fr);
  gap: ${({ $gap }) => $gap || '4px'};
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 600px) {
    grid-template-columns: repeat(${({ $columnsDesktop }) => $columnsDesktop || 4}, 1fr);
    gap: ${({ $gapDesktop }) => $gapDesktop || '6px'};
  }
`;

const GridItem = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* 1:1 aspect ratio */
  border-radius: ${({ $borderRadius }) => $borderRadius || '8px'};
  overflow: hidden;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.bg3};
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LockedPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.bg3} 0%, 
    ${({ theme }) => theme.colors.bg2} 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const LockedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const LockIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const PriceTag = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  padding: 0.25rem 0.5rem;
  border-radius: 10px;
`;

const Badge = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const UnlockedBadge = styled(Badge)`
  top: 6px;
  right: 6px;
  background: ${({ theme }) => theme.colors.success};
`;

const VideoIndicator = styled(Badge)`
  bottom: 6px;
  left: 6px;
  background: rgba(0, 0, 0, 0.7);
`;

const DurationBadge = styled.span`
  position: absolute;
  bottom: 6px;
  right: 6px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
`;

const AlertIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.warning || '#FFA500'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

/**
 * Reusable Media Gallery Grid component
 * 
 * @param {Array} items - Array of media items with: id, url, thumbnailUrl, mimeType, isPaid, isUnlocked, price
 * @param {Function} onItemClick - Callback when item is clicked, receives (item, index)
 * @param {number} columns - Number of columns on mobile (default: 3)
 * @param {number} columnsDesktop - Number of columns on desktop (default: 4)
 * @param {string} gap - Gap between items on mobile (default: '4px')
 * @param {string} gapDesktop - Gap between items on desktop (default: '6px')
 * @param {string} borderRadius - Border radius for items (default: '8px')
 * @param {boolean} showLockOverlay - Whether to show lock overlay for locked items (default: true)
 * @param {boolean} showUnlockedBadge - Whether to show unlocked badge for paid unlocked items (default: true)
 * @param {boolean} showVideoIndicator - Whether to show video indicator (default: true)
 * @param {Function} renderOverlay - Custom render function for item overlay, receives (item, index)
 */
const MediaGalleryGrid = ({
  items = [],
  onItemClick,
  columns = 3,
  columnsDesktop = 4,
  gap = '4px',
  gapDesktop = '6px',
  borderRadius = '8px',
  showLockOverlay = true,
  showUnlockedBadge = true,
  showVideoIndicator = true,
  renderOverlay,
}) => {
  const isVideo = (mimeType) => mimeType?.startsWith('video/') || mimeType?.startsWith('video');

  // Determine if an item is truly locked (paid content that's not unlocked AND has a price > 0)
  const isItemLocked = (item) => {
    const isPaid = item.isPaid ?? item.paid ?? false;
    const isUnlocked = item.isUnlocked ?? item.unlocked ?? false;
    const price = item.price ?? 0;
    return isPaid && !isUnlocked && price > 0;
  };

  return (
    <Grid 
      $columns={columns} 
      $columnsDesktop={columnsDesktop}
      $gap={gap}
      $gapDesktop={gapDesktop}
    >
      {items.map((item, index) => {
        const locked = isItemLocked(item);
        const hasMedia = item.thumbnailUrl || item.url;
        
        return (
          <GridItem
            key={item.id || index}
            $borderRadius={borderRadius}
            onClick={() => onItemClick?.(item, index)}
          >
            {/* Show thumbnail if available, or locked placeholder only if truly locked with a price */}
            {hasMedia ? (
              <Thumbnail
                src={item.thumbnailUrl || item.url}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : locked ? (
              <LockedPlaceholder>
                <LockIconWrapper>
                  <VipIcon name="Lock" size={16} />
                </LockIconWrapper>
                <PriceTag>${item.price?.toFixed(2) || '0.00'}</PriceTag>
              </LockedPlaceholder>
            ) : (
              <LockedPlaceholder>
                <AlertIconWrapper>
                  <VipIcon name="AlertIcon" size={16} />
                </AlertIconWrapper>
              </LockedPlaceholder>
            )}

            {/* Video indicator - only for unlocked content */}
            {showVideoIndicator && isVideo(item.mimeType) && hasMedia && (
              <VideoIndicator>
                <VipIcon name="VideoIcon" size={14} />
              </VideoIndicator>
            )}

            {/* Duration (MM:SS) for videos */}
            {showVideoIndicator && isVideo(item.mimeType) && hasMedia && formatMediaDuration(item.duration) && (
              <DurationBadge>{formatMediaDuration(item.duration)}</DurationBadge>
            )}

            {/* Unlocked badge for paid content that's unlocked */}
            {showUnlockedBadge && (item.isPaid ?? item.paid) && (item.isUnlocked ?? item.unlocked) && (
              <UnlockedBadge>
                <VipIcon name="UnlockIcon" size={14} />
              </UnlockedBadge>
            )}

            {/* Custom overlay */}
            {renderOverlay?.(item, index)}
          </GridItem>
        );
      })}
    </Grid>
  );
};

export default MediaGalleryGrid;

// Also export individual styled components for custom compositions
export { Grid, GridItem, Thumbnail, LockedPlaceholder, LockedOverlay, LockIconWrapper, PriceTag, UnlockedBadge, VideoIndicator, AlertIconWrapper };
